import type { ApplicationStatus } from '@core/types';
import { PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';

/**
 * Phase 8 T-01 §2E — dot colours.
 * Emerald-500 (#10B981) = success / completed / offer / placed / shortlisted.
 * Amber-500  (#F59E0B) = in-progress / screening / interview.
 * Blue-500   (#3B82F6) = submitted / viewed / published.
 * Red-500    (#EF4444) = rejected / failed / no_show / cancelled.
 * Zinc-400   (#9CA3AF) = on-hold / draft / paused / queued / closed.
 */

/** 6px dot colour for free-text / email / AI reviewer hints (case-insensitive substring rules). */
export function statusDotColorFromHint(hint: string): string {
  const s = hint.toLowerCase();
  if (
    s.includes('rejected') ||
    s.includes('bounced') ||
    s.includes('failed') ||
    s.includes('no_show') ||
    s.includes('cancelled')
  ) {
    return '#EF4444';
  }
  if (
    s.includes('offer') ||
    s.includes('completed') ||
    s.includes('assessed') ||
    s.includes('sent') ||
    s.includes('placed') ||
    s.includes('shortlisted')
  ) {
    return '#10B981';
  }
  if (
    s.includes('screening') ||
    s.includes('interview') ||
    s.includes('in progress') ||
    s.includes('in_progress') ||
    s.includes('scheduled') ||
    s.includes('progress')
  ) {
    return '#F59E0B';
  }
  if (s.includes('submitted') || s.includes('viewed') || s.includes('opened') || s.includes('published')) {
    return '#3B82F6';
  }
  if (
    s.includes('on hold') ||
    s.includes('on_hold') ||
    s.includes('paused') ||
    s.includes('draft') ||
    s.includes('queued') ||
    s.includes('closed')
  ) {
    return '#9CA3AF';
  }
  return '#9CA3AF';
}

export function statusDotColorForApplicationStatus(status: ApplicationStatus): string {
  switch (status) {
    case 'submitted':
    case 'viewed_by_client':
      return '#3B82F6';
    case 'shortlisted':
      return '#F59E0B';
    case 'interview_1_scheduled':
    case 'interview_1_conducted':
    case 'interview_2_scheduled':
    case 'interview_2_conducted':
      return '#F59E0B';
    case 'offer_extended':
    case 'offer_accepted':
    case 'placed':
      return '#10B981';
    case 'offer_rejected':
    case 'rejected':
    case 'no_show':
      return '#EF4444';
    case 'on_hold':
    case 'closed':
      return '#9CA3AF';
    default:
      return '#9CA3AF';
  }
}

/**
 * Phase 8 T-01 §2E: transparent background, 6px dot, label.
 * No filled pill — Wrangle style: "Status is shown with text + an inline dot, never filled pills."
 */
const CHIP = 'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium max-w-full min-w-0';
const CHIP_BG = 'bg-transparent vscode-text-secondary';

export function StatusChip({
  label,
  dotColor,
  title,
}: {
  label: string;
  dotColor: string;
  title?: string;
}) {
  return (
    <span className={`${CHIP} ${CHIP_BG}`} title={title}>
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: dotColor }}
        aria-hidden
      />
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

export function ApplicationStatusChip({ status }: { status: ApplicationStatus }) {
  const label = PIPELINE_STATUS_LABEL[status] ?? status;
  return <StatusChip label={label} dotColor={statusDotColorForApplicationStatus(status)} title={status} />;
}
